import type { BlockStdScope, Command } from '@blocksuite/std';
import {
  type BlockModel,
  Slice,
  type TransformerMiddleware,
} from '@blocksuite/store';

import {
  copyBlockIndexReviewSubject,
  type EmbeddingIndexReviewSubjectKind,
} from '../../services/embedding-status-service';
import { draftSelectedModelsCommand } from './draft-selected-models';

type FileIndexReviewSubjectKind = Extract<
  EmbeddingIndexReviewSubjectKind,
  'attachment-block' | 'image-block'
>;

interface FileIndexReviewSource {
  blockId: string;
  blobId: string;
  subjectKind: FileIndexReviewSubjectKind;
}

/**
 * @description Duplicate the selected models
 * @param selectedModels - The selected models for duplicate
 * @param parentModel - The parent model of duplicated models, default is the last selected model's parent model
 * @param index - The index of the duplicated models in the parent model, default is the last selected model's index + 1
 */
export const duplicateSelectedModelsCommand: Command<{
  selectedModels?: BlockModel[];
  parentModel?: BlockModel;
  index?: number;
}> = (ctx, next) => {
  const { std, selectedModels } = ctx;
  let { parentModel, index } = ctx;
  if (!selectedModels) return;

  const [_, { draftedModels }] = ctx.std.command.exec(
    draftSelectedModelsCommand,
    { selectedModels }
  );
  if (!draftedModels) return;

  if (parentModel) {
    if (
      index === undefined ||
      index < 0 ||
      index >= parentModel.children.length
    ) {
      index = parentModel.children.length;
    }
  } else {
    const model = selectedModels[selectedModels.length - 1];
    parentModel = std.store.getParent(model.id) ?? undefined;
    if (!parentModel) return;
    index = parentModel.children.findIndex(x => x.id === model.id) + 1;
  }

  const disposeIndexReviewCopy = installIndexReviewDuplicateCopyMiddleware(
    std,
    selectedModels
  );

  draftedModels
    .then(async models => {
      const slice = Slice.fromModels(std.store, models);
      try {
        await std.clipboard.duplicateSlice(
          slice,
          std.store,
          parentModel.id,
          index
        );
      } finally {
        disposeIndexReviewCopy?.();
      }
    })
    .catch(error => {
      disposeIndexReviewCopy?.();
      console.error(error);
    });

  return next();
};

function installIndexReviewDuplicateCopyMiddleware(
  std: BlockStdScope,
  selectedModels: BlockModel[]
): (() => void) | null {
  const sources = collectFileIndexReviewSources(selectedModels);
  if (sources.length === 0) return null;

  const pending = [...sources];
  const sourceByBlockId = new Map(
    sources.map(source => [source.blockId, source] as const)
  );
  const sourceBySnapshot = new WeakMap<object, FileIndexReviewSource>();

  const middleware: TransformerMiddleware = ({ slots }) => {
    const beforeImportSubscription = slots.beforeImport.subscribe(payload => {
      if (payload.type !== 'block') return;
      const source = sourceByBlockId.get(payload.snapshot.id);
      if (!source) return;
      sourceBySnapshot.set(payload.snapshot, source);
    });

    const afterImportSubscription = slots.afterImport.subscribe(payload => {
      if (payload.type !== 'block') return;
      const target = getFileIndexReviewSource(payload.model as BlockModel);
      if (!target) return;

      const source =
        sourceBySnapshot.get(payload.snapshot) ??
        takeMatchingPendingSource(pending, target);
      if (!source || source.blockId === target.blockId) return;
      removePendingSource(pending, source);

      copyBlockIndexReviewSubject({
        std,
        subjectKind: source.subjectKind,
        sourceBlockId: source.blockId,
        targetBlockId: target.blockId,
        blobId: target.blobId,
      });
    });

    return () => {
      beforeImportSubscription.unsubscribe();
      afterImportSubscription.unsubscribe();
    };
  };

  std.clipboard.use(middleware);
  return () => std.clipboard.unuse(middleware);
}

function collectFileIndexReviewSources(
  models: readonly BlockModel[]
): FileIndexReviewSource[] {
  const sources: FileIndexReviewSource[] = [];
  const visit = (model: BlockModel) => {
    const source = getFileIndexReviewSource(model);
    if (source) sources.push(source);
    for (const child of model.children) {
      visit(child);
    }
  };

  for (const model of models) {
    visit(model);
  }

  return sources;
}

function getFileIndexReviewSource(
  model: BlockModel
): FileIndexReviewSource | null {
  const subjectKind = getFileIndexReviewSubjectKind(model);
  if (!subjectKind) return null;

  const blobId = getModelSourceId(model);
  if (!blobId) return null;

  return {
    blockId: model.id,
    blobId,
    subjectKind,
  };
}

function getFileIndexReviewSubjectKind(
  model: BlockModel
): FileIndexReviewSubjectKind | null {
  if (model.flavour === 'affine:image') return 'image-block';
  if (model.flavour === 'affine:attachment') return 'attachment-block';
  return null;
}

function getModelSourceId(model: BlockModel): string | null {
  const props = model.props as {
    sourceId?: unknown;
    sourceId$?: { value?: unknown; peek?: () => unknown };
  };
  const signalValue =
    typeof props.sourceId$?.peek === 'function'
      ? props.sourceId$.peek()
      : props.sourceId$?.value;
  const value = typeof signalValue === 'string' ? signalValue : props.sourceId;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function removePendingSource(
  pending: FileIndexReviewSource[],
  source: FileIndexReviewSource
): void {
  const index = pending.findIndex(
    candidate => candidate.blockId === source.blockId
  );
  if (index !== -1) {
    pending.splice(index, 1);
  }
}

function takeMatchingPendingSource(
  pending: FileIndexReviewSource[],
  target: FileIndexReviewSource
): FileIndexReviewSource | null {
  const index = pending.findIndex(
    source =>
      source.subjectKind === target.subjectKind &&
      source.blobId === target.blobId &&
      source.blockId !== target.blockId
  );
  if (index === -1) return null;
  const [source] = pending.splice(index, 1);
  return source ?? null;
}
